import { useInitialLoading } from '../hooks/useInitialLoading';
import { ProtectedFileLink } from '../components/ProtectedFile';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { API, Badge, tableStyle, thStyle, tdStyle, inputStyle, btnPrimary, btnSecondary, btnSmall, overlayStyle, modalStyle, FormRow, DetailField } from './AcademyShared';

export default function AcademyDocuments() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [list, setList] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [includeSensitive, setIncludeSensitive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [showExpiring, setShowExpiring] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/companies/${companyId}/academy/documents?per_page=50`;
      if (includeSensitive) url += `&include_sensitive=1`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      const json = await res.json();
      setList(Array.isArray(json.data) ? json.data : []);
    } catch (e) { console.error(e); setList([]); }
    setLoading(false);
  }, [companyId, includeSensitive]);

  useEffect(() => { fetchList(); }, [fetchList]);

  useEffect(() => {
    fetch(`${API}/companies/${companyId}/academy/documents/expiring?days=30`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : { data: [] }).then(j => setExpiring(Array.isArray(j.data) ? j.data : (Array.isArray(j) ? j : []))).catch(() => setExpiring([]));
  }, [companyId]);

  const changeStatus = async (id, status) => {
    await fetch(`${API}/companies/${companyId}/academy/documents/${id}/status`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    fetchList();
  };

  const archiveDoc = async (id) => {
    if (!window.confirm('Archive this document?')) return;
    const res = await fetch(`${API}/companies/${companyId}/academy/documents/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Cannot archive'); return; }
    fetchList();
  };

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>📄 Documents</h2>
        <div style={{ display: 'flex', gap: 10 }}>
          <button style={btnSecondary} onClick={() => setShowExpiring(v => !v)}>{showExpiring ? 'Hide' : 'Show'} Expiring ({expiring.length})</button>
          <button style={btnPrimary} onClick={() => setUploading(true)}>+ Upload Document</button>
        </div>
      </div>

      {showExpiring && (
        <div style={{ background: '#fff', border: '1px solid #fed7aa', borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead><tr>{['Name', 'Expiry Date', 'Status'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
              <tbody>
                {expiring.length === 0 && <tr><td colSpan={3} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>Nothing expiring soon</td></tr>}
                {expiring.map(d => (
                  <tr key={d.id}><td style={tdStyle}>{d.title}</td><td style={tdStyle}>{d.expiry_date}</td><td style={tdStyle}><Badge value={d.status} /></td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={includeSensitive} onChange={e => setIncludeSensitive(e.target.checked)} /> Include sensitive documents
        </label>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{['Name', 'Entity Type', 'Version', 'Sensitive', 'Expiry', 'Status', 'Actions'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {initialLoading && <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No documents</td></tr>}
              {list.map(d => (
                <tr key={d.id}>
                  <td style={{ ...tdStyle, fontWeight: 600, cursor: 'pointer', color: '#3b82f6' }} onClick={() => setViewing(d)}>{d.title}</td>
                  <td style={tdStyle}>{d.entity_type}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>v{d.latest_version?.version_number || 1}</td>
                  <td style={tdStyle}>{d.is_sensitive ? 'Yes' : 'No'}</td>
                  <td style={tdStyle}>{d.expiry_date || '—'}</td>
                  <td style={tdStyle}><Badge value={d.status} /></td>
                  <td style={tdStyle}>
                    <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => setViewing(d)}>Versions</button>
                    {d.status !== 'active' && d.status !== 'archived' && <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => changeStatus(d.id, 'active')}>Activate</button>}
                    {d.status !== 'archived' && <button style={{ ...btnSmall, color: '#ef4444' }} onClick={() => archiveDoc(d.id)}>Archive</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {uploading && <UploadModal companyId={companyId} onClose={() => setUploading(false)} onSaved={() => { setUploading(false); fetchList(); }} />}
      {viewing && <DocumentDetail companyId={companyId} documentId={viewing.id} onClose={() => { setViewing(null); fetchList(); }} />}
    </div>
  );
}

function UploadModal({ companyId, onClose, onSaved }) {
  const [form, setForm] = useState({ title: '', entity_type: 'general', document_type: '', expiry_date: '', is_sensitive: false });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.title.trim() || !file) { alert('Name and file are required'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append('file', file);
      const headers = getAuthHeaders();
      delete headers['Content-Type'];
      const res = await fetch(`${API}/companies/${companyId}/academy/documents`, { method: 'POST', headers, body: fd });
      if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Upload failed'); setSaving(false); return; }
      onSaved();
    } catch (e) { console.error(e); setSaving(false); }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Upload Document</h3>
        <FormRow label="Name *"><input style={inputStyle} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></FormRow>
        <FormRow label="Entity Type *">
          <select style={inputStyle} value={form.entity_type} onChange={e => setForm({ ...form, entity_type: e.target.value })}>
            {['general', 'student', 'enrollment', 'bootcamp_type', 'cohort', 'instructor', 'assessment', 'certificate'].map(c => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
          </select>
        </FormRow>
        <FormRow label="Document Type (optional)"><input style={inputStyle} value={form.document_type} onChange={e => setForm({ ...form, document_type: e.target.value })} /></FormRow>
        <FormRow label="Expiry Date (optional)"><input type="date" style={inputStyle} value={form.expiry_date} onChange={e => setForm({ ...form, expiry_date: e.target.value })} /></FormRow>
        <FormRow label="File *"><input type="file" onChange={e => setFile(e.target.files[0])} /></FormRow>
        <FormRow label="">
          <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={form.is_sensitive} onChange={e => setForm({ ...form, is_sensitive: e.target.checked })} /> Mark as sensitive
          </label>
        </FormRow>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Uploading...' : 'Upload'}</button>
        </div>
      </div>
    </div>
  );
}

function DocumentDetail({ companyId, documentId, onClose }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [uploadingVersion, setUploadingVersion] = useState(false);
  const [file, setFile] = useState(null);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/academy/documents/${documentId}`, { headers: getAuthHeaders() });
      setDoc(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [companyId, documentId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const uploadVersion = async () => {
    if (!file) { alert('Select a file'); return; }
    const fd = new FormData();
    fd.append('file', file);
    const headers = getAuthHeaders();
    delete headers['Content-Type'];
    const res = await fetch(`${API}/companies/${companyId}/academy/documents/${documentId}/versions`, { method: 'POST', headers, body: fd });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Upload failed'); return; }
    setUploadingVersion(false);
    setFile(null);
    fetchDetail();
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, width: 600 }} onClick={e => e.stopPropagation()}>
        {initialLoading || !doc ? <div>Loading...</div> : (
          <>
            <h3 style={{ marginTop: 0 }}>{doc.title} <Badge value={doc.status} /></h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
              <DetailField label="Entity Type" value={doc.entity_type} />
              <DetailField label="Sensitive" value={doc.is_sensitive ? 'Yes' : 'No'} />
              <DetailField label="Expiry" value={doc.expiry_date} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h4 style={{ margin: 0 }}>Versions</h4>
              <button style={btnSmall} onClick={() => setUploadingVersion(v => !v)}>{uploadingVersion ? 'Cancel' : '+ New Version'}</button>
            </div>
            {uploadingVersion && (
              <div style={{ display: 'flex', gap: 10, marginBottom: 12, alignItems: 'center' }}>
                <input type="file" onChange={e => setFile(e.target.files[0])} />
                <button style={btnPrimary} onClick={uploadVersion}>Upload</button>
              </div>
            )}
            <table style={tableStyle}>
              <thead><tr>{['Version', 'Uploaded', 'File'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
              <tbody>
                {(doc.versions || []).length === 0 && <tr><td colSpan={3} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No versions</td></tr>}
                {(doc.versions || []).map(v => (
                  <tr key={v.id}>
                    <td style={tdStyle}>v{v.version_number}</td>
                    <td style={tdStyle}>{v.created_at ? new Date(v.created_at).toLocaleDateString() : '—'}</td>
                    <td style={tdStyle}>{v.file_path ? <ProtectedFileLink path={v.file_path} /> : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button style={btnSecondary} onClick={onClose}>Close</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
